import clinicalIntakeService from '../services/clinicalIntake.service';
import administrativeInfoService from '../services/administrativeInfo.service';
import { projectService } from '../services/projectService';
import { resolveAdminSponsorFields } from './administrativeSponsorFields';
import { resolveProjectSponsorName, resolveSponsorProtocolIdForDisplay } from './projectIdentityFields';

/** DOCX header row 1 (left): investigational product name(s), else sponsor product code(s). */
export const resolveHeaderProductDisplay = (names, codes) => {
  const normalize = (v) => String(v ?? '').trim();
  const isNotApplicable = (v) => {
    const s = normalize(v).toLowerCase();
    return !s || s === 'n/a' || s === 'na' || s === 'not applicable' || s === 'not app' || s === 'none';
  };

  if (!isNotApplicable(names)) return normalize(names);
  if (!isNotApplicable(codes)) return normalize(codes);
  return '';
};

/**
 * Protocol number for the running header. Clinical intake wins when it has one;
 * otherwise use the same identifier the title page shows (project studyId, then
 * Admin Info sponsor_protocol_identifier). USDM-generated studies usually have no
 * intake protocol_number, so without the fallback their headers were blank.
 */
export const pickHeaderProtocolNumber = ({ clinicalIntake, titlePageConfig, project, adminInfo } = {}) =>
  String(
    clinicalIntake?.study_identification?.protocol_number ||
      clinicalIntake?.protocol_number ||
      titlePageConfig?.sponsorProtocolIdentifier?.text ||
      resolveSponsorProtocolIdForDisplay(project, adminInfo) ||
      '',
  ).trim();

const formatVersionDate = (raw) => {
  if (!raw) return '';
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    const day = date.getDate().toString().padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${day} ${monthNames[date.getMonth()]} ${date.getFullYear()}`;
  }
  return String(raw).trim();
};

/**
 * Resolve running header/footer fields for protocol export (Word §4.6 / native PDF).
 * Row 1: product (left) | sponsor (right)
 * Row 2: protocol number (left) | version date (right)
 * Footer: Confidential (left) | Page X of Y (right, rendered in PDF)
 */
export async function resolveProtocolHeaderFooterInfo(projectId, titlePageConfig = null) {
  const empty = {
    protocolNumber: '',
    versionDate: '',
    sponsorName: '',
    investigationalProductNames: '',
  };

  if (!projectId) return empty;

  let protocolNumber = '';
  let versionDate = '';
  let sponsorName = '';
  let investigationalProductNames = resolveHeaderProductDisplay(
    titlePageConfig?.investigationalProductNames?.text,
    titlePageConfig?.sponsorProductCodes?.text,
  );

  try {
    let clinicalIntakeData = null;
    let projectData = null;
    let adminInfo = null;
    try {
      clinicalIntakeData = await clinicalIntakeService.getLatestClinicalIntake(projectId);
    } catch (clinicalErr) {
      console.warn('Could not fetch clinical intake for PDF header/footer:', clinicalErr);
    }

    try {
      const adminResponse = await administrativeInfoService.getAllAdministrativeInfo(projectId);
      let projectSponsorName = '';
      try {
        const projectRes = await projectService.getProject(projectId);
        projectData = projectRes?.data ?? projectRes;
        projectSponsorName = resolveProjectSponsorName(projectData);
      } catch (projectErr) {
        console.warn('Could not fetch project for PDF header sponsor name:', projectErr);
      }

      if (adminResponse?.data?.length > 0) {
        adminInfo = adminResponse.data[0];
        sponsorName = String(
          projectSponsorName || resolveAdminSponsorFields(adminInfo).sponsorName,
        ).trim();
        if (!investigationalProductNames) {
          investigationalProductNames = resolveHeaderProductDisplay(
            adminInfo.investigational_product_names,
            adminInfo.sponsor_investigational_product_codes,
          );
        }
        versionDate = formatVersionDate(adminInfo.version_date);
      }
    } catch (adminErr) {
      console.warn('Could not fetch administrative info for PDF header/footer:', adminErr);
    }

    protocolNumber = pickHeaderProtocolNumber({
      clinicalIntake: clinicalIntakeData,
      titlePageConfig,
      project: projectData,
      adminInfo,
    });
  } catch (err) {
    console.warn('Could not resolve protocol header/footer info:', err);
  }

  return {
    protocolNumber,
    versionDate,
    sponsorName,
    investigationalProductNames,
  };
}

export const EMPTY_PROTOCOL_HEADER_FOOTER = {
  protocolNumber: '',
  versionDate: '',
  sponsorName: '',
  investigationalProductNames: '',
};
